import { downloadGeoJSON, featureCollection } from '../../common/geojson';
import { baselineFeature, coastlineFeatures } from '../utils/coastlineGeoJson';
import { reprojectFeatureCollection } from './reprojection';

/**
 * @constant GEOJSON_MODULE
 * @description Module key, used to name the downloaded file.
 * @memberof module:ShoreS
 */
const GEOJSON_MODULE = 'shore_s';

/**
 * @constant WGS84
 * @description The CRS GeoJSON is defined in. Everything written out has to arrive in it.
 * @memberof module:ShoreS
 */
const WGS84 = 'EPSG:4326';

/**
 * @function toWgs84
 * @description Brings a collection into WGS84, leaving it alone when it is already there.
 * Shore-S runs its analysis in whatever CRS the uploaded model declares, and keeps the reference
 * coastlines in that working CRS - the map reprojects them on the way to being drawn. Written out
 * as they are stored, a set of UTM eastings and northings reads as longitude and latitude, which
 * puts the coastline thousands of kilometres away or outside the valid range entirely.
 * @param {Object} [collection] - A FeatureCollection in `crs`.
 * @param {String} [crs] - The CRS it is held in.
 * @memberof module:ShoreS
 * @returns {Object|null} The collection in WGS84, or `null` when it cannot be brought over.
 */
const toWgs84 = (collection, crs) => {
  if (!collection?.features?.length) return null;
  if (!crs || /^(EPSG:4326|WGS84)$/i.test(String(crs).trim())) return collection;

  try {
    return reprojectFeatureCollection(collection, crs, WGS84);
  } catch (error) {
    console.error('[ShoreS] could not reproject a layer for export', error);
    return null;
  }
};

/**
 * @function buildGeoJsonExport
 * @description Gathers a finished Shore-S validation into one FeatureCollection: the baseline, the
 * coastlines the model produced, and the satellite-derived ones they were compared against, each
 * in its own layer so the two can be styled apart and measured against each other in a GIS.
 * The three arrive in different CRSs, which is the whole difficulty here. The baseline is drawn on
 * the map and so is already WGS84; the modelled coastlines are passed in already reprojected,
 * because the page keeps that form to draw them; the reference coastlines are held in the model's
 * working CRS and are brought over here.
 * @param {Object} params
 * @param {Array<Array<Number>>} [params.baselineCoords] - Baseline vertices, in WGS84.
 * @param {Object} [params.modeledCoastlines] - Modelled coastlines, already in WGS84.
 * @param {Object} [params.referenceCoastlines] - Reference coastlines, in `crs`.
 * @param {String} [params.crs] - The model's working CRS, e.g. `'EPSG:32722'`.
 * **Called by:
 * @see {ProcessingPage} - Shore-S's result page, through the GIS export icon.
 * @memberof module:ShoreS
 * @returns {Object} A FeatureCollection, empty when the analysis has produced nothing yet.
 */
export const buildGeoJsonExport = ({ baselineCoords, modeledCoastlines, referenceCoastlines, crs }) =>
  featureCollection([
    baselineFeature(baselineCoords),
    coastlineFeatures(modeledCoastlines, 'modeled_coastlines'),
    coastlineFeatures(toWgs84(referenceCoastlines, crs), 'reference_coastlines'),
  ]);

/**
 * @function exportGeoJson
 * @description Builds the collection and hands it to the browser.
 * @param {Object} params - Same as {@link buildGeoJsonExport}.
 * **Called by:
 * @see {ProcessingPage} - Shore-S's result page.
 * @memberof module:ShoreS
 * @returns {Boolean} False when there was nothing to write.
 */
export const exportGeoJson = (params) => downloadGeoJSON(buildGeoJsonExport(params), GEOJSON_MODULE);
